// Implements: F-UIX-019, F-UIX-007, F-GEN-036
/**
 * The model settings as the redesign's page (UX-11): the next-turn notice, two tier cards (model,
 * output cap with an explicit "Sin límite" switch, reasoning effort), the prices of every chosen
 * model, the conversation's message maximum, the read-only technical limits, and a sticky action
 * bar with the cost per turn, the restore and the save.
 *
 * It holds no state but which catalogue picker is open. The model is a free-text combobox whose
 * suggestions are the models the configuration already names; since mockup v3 P7 each tier also
 * opens the provider catalogue («Buscar en el catálogo», a popover whose content the container
 * gives through `renderPicker`), and the prices it fills are tagged «del catálogo», then
 * «editado» once typed over. Typing the model by hand keeps working when the catalogue does not
 * answer. A cap is a number
 * or explicitly "Sin límite" (a switch; the field is then disabled and the value is sent unset).
 * Each refused field carries its own sentence (`role="alert"`, wired by `aria-describedby` with
 * its hint) and, when the container asks (`focusToken`), the focus goes to the first refused
 * control. The save button is the shared `Button`, which stays focusable and refuses a second
 * press while it loads. A conflict notice takes the focus when it appears; the container owns the
 * confirmation and the cost preview.
 */

import { useEffect, useId, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";

import { useTranslation } from "../../../shared/i18n";
import type { TranslationKey } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Card, CardHeader } from "../../../shared/ui/atoms/Card";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { Input } from "../../../shared/ui/atoms/Input";
import { Switch } from "../../../shared/ui/atoms/Switch";
import { byteText } from "../../../shared/ui/byteText";
import { Combobox } from "../../../shared/ui/molecules/Combobox";
import type { ComboboxOption } from "../../../shared/ui/molecules/Combobox";
import { Field, describedBy, errorId } from "../../../shared/ui/molecules/Field";
import { Popover } from "../../../shared/ui/molecules/Popover";
import { SegmentedControl } from "../../../shared/ui/molecules/SegmentedControl";
import { REASONING_EFFORTS } from "../../../types/modelSettings";
import type { ModelSettingsView, ReasoningEffort, TierName } from "../../../types/modelSettings";
import { priceTagOf } from "../model/catalogueFill";
import { cleanModelId, priceField } from "../model/modelSettingsForm";
import type { FieldErrorKind, FieldErrors, SettingsDraft, TierDraft } from "../model/modelSettingsForm";
import styles from "./ModelSettings.module.css";

const TIERS: readonly TierName[] = ["fast", "capable"];

const EFFORT_LABEL = {
  minimal: "admin.modelSettings.effort.minimal",
  low: "admin.modelSettings.effort.low",
  medium: "admin.modelSettings.effort.medium",
  high: "admin.modelSettings.effort.high",
} as const satisfies Record<ReasoningEffort, TranslationKey>;

const ERROR_LABEL = {
  required: "admin.modelSettings.error.required",
  positive_integer: "admin.modelSettings.error.positiveInteger",
  same_model: "admin.modelSettings.error.sameModel",
  price_required: "admin.modelSettings.error.priceRequired",
  price_invalid: "admin.modelSettings.error.priceInvalid",
  unknown_model: "admin.modelSettings.error.unknownModel",
  unpriced_model: "admin.modelSettings.error.unpricedModel",
  above_model_maximum: "admin.modelSettings.error.aboveModelMaximum",
  too_many_prices: "admin.modelSettings.error.tooManyPrices",
  malformed_model_id: "admin.modelSettings.error.malformedModelId",
  rejected: "admin.modelSettings.error.rejected",
} as const satisfies Record<FieldErrorKind, TranslationKey>;

/** The first refused control: an invalid field, or the pressed segment of a refused effort. */
const FIRST_REFUSED = '[aria-invalid="true"], [data-refused] [aria-pressed="true"]';

export interface ModelSettingsFormProps {
  readonly draft: SettingsDraft;
  /** The models the form prices: the distinct ones the two tiers choose. */
  readonly models: readonly string[];
  /** What the model fields suggest (never a closed list). */
  readonly suggestions: readonly ComboboxOption[];
  readonly limits: ModelSettingsView["technical_limits"];
  readonly errors: FieldErrors;
  /** A save is in flight. */
  readonly saving: boolean;
  /** A restore is in flight. */
  readonly restoring: boolean;
  /** Bumped when the focus must go to the first refused control. */
  readonly focusToken: number;
  /** Bumped when the refusal itself takes the focus (it names no field the form shows). */
  readonly refusalToken: number;
  /** The server's refusal, as the admin reads it, or `null`. */
  readonly refusal: string | null;
  /** Somebody saved first: the revision now stored, or `null`. */
  readonly conflict: number | null;
  /** The sentence of the last successful write, or `null`. */
  readonly saved: string | null;
  /** The cost line and its preview control, drawn at the start of the action bar. */
  readonly cost: ReactNode;
  readonly onTier: (tier: TierName, patch: Partial<TierDraft>) => void;
  readonly onPrice: (model: string, part: "prompt" | "completion", value: string) => void;
  readonly onMessage: (patch: { readonly message?: string; readonly noLimit?: boolean }) => void;
  readonly onSubmit: () => void;
  readonly onRestore: () => void;
  readonly onReload: () => void;
  /** The catalogue picker of a tier; `close` shuts its popover. Without it no picker is offered. */
  readonly renderPicker?: ((tier: TierName, close: () => void) => ReactNode) | undefined;
}

interface CatalogueButtonProps {
  readonly tier: TierName;
  readonly disabled: boolean;
  readonly renderPicker: (tier: TierName, close: () => void) => ReactNode;
}

/**
 * «Buscar en el catálogo» and the popover it opens. Escape gives the focus back to the button (the
 * popover's own rule), and so does a pick, which closes it (`picked`).
 */
function CatalogueButton({ tier, disabled, renderPicker }: CatalogueButtonProps): ReactElement {
  const { t } = useTranslation();
  const anchor = useRef<HTMLButtonElement | null>(null);
  const [state, setState] = useState<"closed" | "open" | "picked">("closed");
  const open = state === "open";

  useEffect(() => {
    if (state === "picked") anchor.current?.focus();
  }, [state]);

  const close = (): void => {
    setState("picked");
  };
  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        hasPopup="dialog"
        expanded={open}
        disabled={disabled}
        buttonRef={anchor}
        onClick={() => {
          setState(open ? "closed" : "open");
        }}
      >
        <Icon name="search" size="sm" />
        {t("models.picker.open")}
      </Button>
      <Popover
        open={open}
        onClose={() => {
          setState("closed");
        }}
        anchorRef={anchor}
        label={t("models.picker.open")}
        width="auto"
        align="start"
      >
        {open ? renderPicker(tier, close) : null}
      </Popover>
    </>
  );
}

export function ModelSettingsForm({
  draft,
  models,
  suggestions,
  limits,
  errors,
  saving,
  restoring,
  focusToken,
  refusalToken,
  refusal,
  conflict,
  saved,
  cost,
  onTier,
  onPrice,
  onMessage,
  onSubmit,
  onRestore,
  onReload,
  renderPicker,
}: ModelSettingsFormProps): ReactElement {
  const { t } = useTranslation();
  const ids = useId();
  const pricesErrorId = `${ids}-prices-error`;
  const formRef = useRef<HTMLFormElement>(null);
  const conflictRef = useRef<HTMLDivElement>(null);
  const refusalRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (focusToken === 0) return;
    formRef.current?.querySelector<HTMLElement>(FIRST_REFUSED)?.focus();
  }, [focusToken]);

  useEffect(() => {
    if (refusalToken !== 0) refusalRef.current?.focus();
  }, [refusalToken]);

  useEffect(() => {
    if (conflict !== null) conflictRef.current?.focus();
  }, [conflict]);

  /** «del catálogo» or «editado» after a model's legend, when the catalogue filled its prices. */
  function priceTag(model: string): ReactElement | null {
    const tag = priceTagOf(draft, model);
    if (tag === undefined) return null;
    return (
      <span className={styles.priceTag} data-edited={tag === "edited" ? "" : undefined}>
        {tag === "edited" ? t("models.price.edited") : t("models.price.fromCatalogue")}
      </span>
    );
  }

  function sentence(key: string): string | undefined {
    const kind = errors[key];
    return kind === undefined ? undefined : t(ERROR_LABEL[kind]);
  }

  /** A whole number with its explicit "Sin límite" switch: the tier caps and the message maximum. */
  function limitRow(input: ReactElement, unit: string | null, noLimit: boolean, onNoLimit: (on: boolean) => void): ReactElement {
    return (
      <div className={styles.limitRow}>
        <span className={styles.limitInput}>{input}</span>
        {unit === null ? null : <span className={styles.unit}>{unit}</span>}
        <Switch label={t("admin.modelSettings.noLimit")} checked={noLimit} disabled={saving} onChange={onNoLimit} />
      </div>
    );
  }

  function capField(name: TierName, tier: TierDraft): ReactElement {
    const id = `${ids}-${name}-cap`;
    const error = sentence(`${name}.cap`);
    const hint = t("admin.modelSettings.capHint");
    return (
      <Field id={id} label={t("admin.modelSettings.cap")} hint={hint} error={error}>
        {limitRow(
          <Input
            id={id}
            value={tier.cap}
            inputMode="numeric"
            invalid={error !== undefined}
            describedBy={describedBy(id, { hint, error })}
            disabled={tier.noLimit}
            readOnly={saving}
            onChange={(value) => {
              onTier(name, { cap: value });
            }}
          />,
          t("admin.modelSettings.capUnit"),
          tier.noLimit,
          (noLimit) => {
            onTier(name, { noLimit });
          },
        )}
      </Field>
    );
  }

  function effortField(name: TierName, tier: TierDraft): ReactElement {
    const id = `${ids}-${name}-effort`;
    const error = sentence(`${name}.effort`);
    return (
      <div className={styles.field} data-refused={error === undefined ? undefined : ""}>
        {/* The group is named by its own label; the visible words are the same, not read twice. */}
        <span className={styles.label} aria-hidden="true">
          {t("admin.modelSettings.effort")}
        </span>
        <SegmentedControl<ReasoningEffort | "">
          label={t("admin.modelSettings.effort")}
          options={[
            { value: "", label: t("admin.modelSettings.effort.none") },
            ...REASONING_EFFORTS.map((effort) => ({ value: effort, label: t(EFFORT_LABEL[effort]) })),
          ]}
          value={tier.effort}
          disabled={saving}
          describedBy={error === undefined ? undefined : errorId(id)}
          onChange={(effort) => {
            onTier(name, { effort });
          }}
        />
        {error === undefined ? null : (
          <p id={errorId(id)} className={styles.error} role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }

  function tierCard(name: TierName): ReactElement {
    const tier = draft[name];
    const headingId = `${ids}-${name}-title`;
    const modelId = `${ids}-${name}-model`;
    const modelError = sentence(`${name}.model`);
    const modelHint = t("admin.modelSettings.modelHint");
    return (
      <Card key={name}>
        <div role="group" aria-labelledby={headingId}>
          <CardHeader>
            <div>
              <h2 id={headingId} className={styles.cardTitle}>
                {t(`admin.modelSettings.tier.${name}`)}
              </h2>
              <p className={styles.meta}>{t(`admin.modelSettings.tier.${name}.description`)}</p>
            </div>
          </CardHeader>
          <div className={styles.cardBody}>
            <Field id={modelId} label={t("admin.modelSettings.model")} hint={modelHint} error={modelError}>
              <Combobox
                id={modelId}
                label={t("admin.modelSettings.model")}
                options={suggestions}
                value={tier.model}
                freeText
                invalid={modelError !== undefined}
                readOnly={saving}
                describedBy={describedBy(modelId, { hint: modelHint, error: modelError })}
                onChange={(model) => {
                  onTier(name, { model: cleanModelId(model) });
                }}
              />
            </Field>
            {renderPicker === undefined ? null : (
              <div className={styles.catalogue}>
                <CatalogueButton tier={name} disabled={saving} renderPicker={renderPicker} />
                <p className={styles.hint}>{t("models.picker.help")}</p>
              </div>
            )}
            {capField(name, tier)}
            {effortField(name, tier)}
          </div>
        </div>
      </Card>
    );
  }

  function priceInput(model: string, index: number, part: "prompt" | "completion"): ReactElement {
    const id = `${ids}-price-${String(index)}-${part}`;
    // A refusal of the whole list (`prices`) marks every price and is described by the card's sentence.
    const error = sentence(priceField(model, part));
    const listError = errors.prices === undefined ? undefined : pricesErrorId;
    const label =
      part === "prompt"
        ? t("admin.modelSettings.prices.prompt", { model })
        : t("admin.modelSettings.prices.completion", { model });
    const group = `${ids}-price-${String(index)}-hint`;
    return (
      <Field id={id} label={label} error={error}>
        <span className={styles.affix}>
          <span className={styles.affixText} aria-hidden="true">
            {t("admin.modelSettings.currency")}
          </span>
          <Input
            id={id}
            value={draft.prices[model]?.[part] ?? ""}
            inputMode="decimal"
            invalid={error !== undefined || listError !== undefined}
            describedBy={[group, error === undefined ? undefined : errorId(id), listError]
              .filter((entry) => entry !== undefined)
              .join(" ")}
            readOnly={saving}
            onChange={(value) => {
              onPrice(model, part, value);
            }}
          />
        </span>
      </Field>
    );
  }

  const pricesId = `${ids}-prices`;
  const pricesError = sentence("prices");
  const messagesId = `${ids}-messages`;
  const limitsId = `${ids}-limits`;
  const messageId = `${ids}-message`;
  const messageError = sentence("message");
  const messageHint = t("admin.modelSettings.messageHint");

  return (
    <form
      ref={formRef}
      noValidate
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div className={styles.notice}>
        <Icon name="info" />
        <p>{t("admin.modelSettings.intro")}</p>
      </div>
      <div className={styles.tiers}>{TIERS.map(tierCard)}</div>
      <Card as="section" labelledBy={pricesId}>
        <CardHeader>
          <h2 id={pricesId} className={styles.cardTitle}>
            {t("admin.modelSettings.prices.title")}
          </h2>
        </CardHeader>
        <div className={styles.cardBody}>
          {pricesError === undefined ? null : (
            <p id={pricesErrorId} className={styles.cardError}>
              <Icon name="alert-circle" size="sm" />
              <span>{pricesError}</span>
            </p>
          )}
          {models.map((model, index) => (
            <fieldset key={model} className={styles.priceGroup}>
              <legend className={styles.legend}>
                {t("admin.modelSettings.prices.model", { model })}
                {priceTag(model)}
              </legend>
              <p id={`${ids}-price-${String(index)}-hint`} className={styles.hint}>
                {t("admin.modelSettings.prices.hint")}
              </p>
              <div className={styles.two}>
                {priceInput(model, index, "prompt")}
                {priceInput(model, index, "completion")}
              </div>
            </fieldset>
          ))}
        </div>
      </Card>
      <Card>
        <div role="group" aria-labelledby={messagesId}>
          <CardHeader>
            <h2 id={messagesId} className={styles.cardTitle}>
              {t("admin.modelSettings.messages.legend")}
            </h2>
          </CardHeader>
          <div className={styles.cardBody}>
            <Field id={messageId} label={t("admin.modelSettings.message")} hint={messageHint} error={messageError}>
              {limitRow(
                <Input
                  id={messageId}
                  value={draft.message}
                  inputMode="numeric"
                  invalid={messageError !== undefined}
                  describedBy={describedBy(messageId, { hint: messageHint, error: messageError })}
                  disabled={draft.messageNoLimit}
                  readOnly={saving}
                  onChange={(value) => {
                    onMessage({ message: value });
                  }}
                />,
                null,
                draft.messageNoLimit,
                (noLimit) => {
                  onMessage({ noLimit });
                },
              )}
            </Field>
          </div>
        </div>
      </Card>
      <Card as="section" labelledBy={limitsId}>
        <CardHeader>
          <h2 id={limitsId} className={styles.cardTitle}>
            {t("admin.modelSettings.limits.title")}
          </h2>
        </CardHeader>
        <div className={styles.cardBody}>
          <p>{t("admin.modelSettings.limits.deadline", { seconds: String(limits.call_deadline_seconds) })}</p>
          <p>{t("admin.modelSettings.limits.body", { size: byteText(limits.http_max_body_bytes) })}</p>
          <p className={styles.hint}>{t("admin.modelSettings.limits.note")}</p>
        </div>
      </Card>
      {conflict === null ? null : (
        <div ref={conflictRef} className={styles.conflict} role="alert" tabIndex={-1}>
          <span>{t("admin.modelSettings.conflict", { revision: String(conflict) })}</span>
          <Button variant="secondary" onClick={onReload}>
            {t("admin.modelSettings.conflict.reload", { revision: String(conflict) })}
          </Button>
        </div>
      )}
      {refusal === null ? null : (
        <p ref={refusalRef} className={styles.refusal} role="alert" tabIndex={-1}>
          {refusal}
        </p>
      )}
      <div className={styles.bar} role="region" aria-label={t("admin.modelSettings.bar")}>
        {cost}
        <span className={styles.spacer} />
        {saved === null ? null : (
          <p className={styles.saved} role="status">
            {saved}
          </p>
        )}
        <Button variant="ghost" loading={restoring} disabled={saving} onClick={onRestore}>
          {t("admin.modelSettings.restore")}
        </Button>
        <Button variant="primary" type="submit" loading={saving} disabled={restoring}>
          {t("admin.modelSettings.save")}
        </Button>
      </div>
    </form>
  );
}
